import { Component, Host, Prop, h, Element, State, Event, EventEmitter } from '@stencil/core';
import { Option, Reservation } from './interfaces';
import BookingTime from '../../assets/svg/calendar-time.svg';
import Calendar from '../../assets/svg/calendar.svg';
import TimeIcon from '../../assets/svg/time.svg';

@Component({
  tag: 'salla-booking-field',
  styleUrl: 'salla-booking-field.scss',
})
export class SallaBookingField {
  /**
   * The booking option configuration
   */
  @Prop() option: Option;
  /**
   * The ID of the product for which the booking is being made
   */
  @Prop() productId: number;
  @Element() host: HTMLElement;
  @State() bookNowLabel: string = salla.lang.get('pages.cart.book_an_appointment', 'حجز موعد');
  @State() editLabel: string = salla.lang.get('pages.cart.edit_an_appointment', 'تعديل الموعد');
  @State() bookedLabel: string = salla.lang.get('pages.cart.booked_successfully', 'تمت اضافة الموعد بنجاح');
  @State() selectDate: string = salla.lang.get('pages.cart.select_appointment_date', 'حدد تاريخ الموعد');
  @State() bookingUrl: string = '';
  @State() iframeReady: boolean = false;
  @State() reservations: Reservation[] = [];
  @State() reservationsInput: HTMLInputElement;

  /**
   * Event emitted when the input is invalid.
   */
  @Event() invalidInput: EventEmitter<any>;

  private modal: HTMLSallaModalElement;
  private iframe: HTMLIFrameElement;

  constructor() {
    // Load translations
    salla.lang.onLoaded(() => this.setTranslations());
    // Register event listeners
    Salla.event.on('booking::open', (data) => this.handleBookingOpen(data));
  }

  private async setTranslations() {
    const setNestedAsync = (lang, key, value) => {
      return new Promise((resolve) => {
        salla.helpers.setNested(salla.lang.messages[lang], key, value);
        resolve(true);
      });
    };

    await setNestedAsync('ar.trans', 'pages.cart.book_an_appointment', 'حجز موعد');
    await setNestedAsync('en.trans', 'pages.cart.book_an_appointment', 'Book an Appointment');
    await setNestedAsync('ar.trans', 'pages.cart.edit_an_appointment', 'تعديل الموعد');
    await setNestedAsync('en.trans', 'pages.cart.edit_an_appointment', 'Edit an Appointment');
    await setNestedAsync('ar.trans', 'pages.cart.booked_successfully', 'تمت اضافة الموعد بنجاح');
    await setNestedAsync('en.trans', 'pages.cart.booked_successfully', 'Booked Successfully');
    await setNestedAsync('ar.trans', 'pages.cart.select_appointment_date', 'حدد تاريخ الموعد');
    await setNestedAsync('en.trans', 'pages.cart.select_appointment_date', 'Select appointment date');

    this.bookNowLabel = salla.lang.get('pages.cart.book_an_appointment');
    this.editLabel = salla.lang.get('pages.cart.edit_an_appointment');
    this.bookedLabel = salla.lang.get('pages.cart.booked_successfully');
    this.selectDate = salla.lang.get('pages.cart.select_appointment_date');
  }

  private openBookingModal(event: any, afterReload = false) {
    if (afterReload && (!event.detail || typeof event.detail !== 'number' || event.detail !== this.productId)) {
      return;
    }
    if (salla.config.isGuest()) {
      this.setAfterReloadEvent('booking::open-after-reload', this.productId);
      salla.event.dispatch('login::open');
      return;
    }
    salla.booking.add(this.productId, false)
      .then((resp) => {
        if (resp.data.redirect.to !== 'booking') {
          throw new Error('Unexpected redirect!');
        }
        salla.event.dispatch('booking::open', { url: resp.data.redirect.url, id: this.productId });
      })
      .catch((error) => {
        salla.error(salla.lang.get('common.errors.error_occurred'));
        salla.logger.error(error.response || error);
      });
  }

  private handleBookingOpen(data) {
    if (data.id !== this.productId) return;
    this.bookingUrl = salla.url.addParamToUrl('product_id', data.id, data.url);
    this.iframeReady = true;
    setTimeout(() => {
      this.modal.setTitle(this.selectDate);
      this.modal.open();
    }, 100);
  }

  private setAfterReloadEvent(event, payload) {
    salla.storage.set('afterReloadEvent', { event, payload });
  }

  private emitAfterReloadEvent() {
    const eventDetails = salla.storage.get('afterReloadEvent');
    if (eventDetails && eventDetails.event) {
      const customEvent = new CustomEvent(eventDetails.event, {
        detail: eventDetails.payload
      });
      window.dispatchEvent(customEvent)
      salla.storage.remove('afterReloadEvent');
    }
  }

  componentWillLoad() {
    if (this.option && this.option.details.length) {
      this.reservations = this.option.details;
    }
  }

  componentDidLoad() {
    window.addEventListener('booking::open-after-reload', (event) => this.openBookingModal(event, true));
    this.emitAfterReloadEvent();
    window.addEventListener('message', this.handleMessageEvent.bind(this));
    this.reservationsInput.addEventListener('invalid', e => this.invalidInput.emit(e));
    this.reservationsInput.addEventListener('input', () => {
      this.reservationsInput.setCustomValidity('');
      this.reservationsInput.reportValidity();
    });
  }

  private handleMessageEvent(event) {
    if (event.data.source !== 'booking') return;
    const action = event.data.type;
    const value = event.data.message;
    if (localStorage.getItem('debug')) console.log(`Received an action:${action}`, event.data);

    if (action === 'error') {
      if (value.fields?.reservation) {
        salla.notify.error(value.fields.reservation[0]);
        return;
      }
      const errorList = Object.values(value.fields || [value.message]).flat().map(error => `<li>${error}</li>`).join('');
      salla.notify.error(`<ul>${errorList}</ul>`);
    }

    if (action === 'success') {
      if (Number(value.productId) !== Number(this.productId)) return;
      this.reservations = value.data.reservations.map(schedule => {
        if (schedule.time && schedule.time.length > 0) {
          const timeSlot = schedule.time[0];
          return {
            date: schedule.date,
            day: schedule.day,
            from_timestamp: timeSlot.from,
            to_timestamp: timeSlot.to,
          };
        }
        return null;
      }).filter(item => item !== null);

      salla.notify.success(this.bookedLabel);
      this.modal?.close();
      setTimeout(() => window.location.reload());
    }

    if (action === 'height') {
      this.iframe.height = value?.height + 'px';
    }
  }

  private bookingModal() {
    return (
      <salla-modal class="s-booking-field-modal" ref={modal => (this.modal = modal)} width="md" position="middle" noPadding>
        <iframe ref={iframe => (this.iframe = iframe)} src={this.bookingUrl} frameborder="0"></iframe>
      </salla-modal>
    );
  }

  renderReservationDate(reservation: Reservation) {
    return (
      <span class={reservation.from_timestamp ? 's-booking-field-reservations-has-time' : ''}>
        <i class="s-booking-field-reservations-icon" innerHTML={Calendar}></i>
        {reservation.date}
      </span>
    );
  }

  renderReservationTime(reservation: Reservation) {
    if (!reservation.from_timestamp) return '';
    return (
      <span class="s-booking-field-reservations-time">
        <i class="s-booking-field-reservations-icon" innerHTML={TimeIcon}></i>
        <span>
          {reservation.from_timestamp} - {reservation.to_timestamp}
        </span>
      </span>
    );
  }

  render() {
    return (
      <Host>
        <div class="s-booking-field-main">
          {this.option.required || this.reservations.length > 0 ? <div class="s-booking-field-price">
            <span>{salla.money(this.option.price)}</span>
          </div> : ''}
          <salla-button class="s-booking-field-book-now" size="small" loaderPosition="center" fill="outline" onClick={event => this.openBookingModal(event, false)}>
            <span class="s-booking-field-book-now-content">
              <span innerHTML={BookingTime}></span>
              {this.reservations.length ? this.editLabel : this.bookNowLabel}
            </span>
          </salla-button>
        </div>
        {this.reservations.length > 0 && (
          <div class="s-booking-field-reservations">
            {this.reservations.map((reservation, index) => (
              <div key={index} class="s-booking-field-reservations-item">
                {this.renderReservationDate(reservation)}
                {this.renderReservationTime(reservation)}
              </div>
            ))}
          </div>
        )}
        <input
          class="s-hidden"
          name={this.option.name}
          required={this.option.required}
          value={JSON.stringify(this.reservations) === '[]' ? '' : JSON.stringify(this.reservations)}
          ref={reservations => this.reservationsInput = reservations}
        />
        {this.iframeReady && this.bookingModal()}
      </Host>
    );
  }
}
